import { useState } from 'react';
import toast from 'react-hot-toast';

import { DictionariesNavigation, useLanguages } from 'features/dictionaries';
import { useVoices, type VoiceResponse } from 'features/generation-lab';
import { getApiErrorMessage } from 'utils/apiErrors';

import { TutorConfigurationDialog } from '../components/TutorConfigurationDialog';
import {
  type LanguagePairConfiguration,
  type TutorConfiguration,
  useDeleteLanguagePairConfiguration,
  useDeleteTutorConfiguration,
  useLanguagePairConfigurations,
  usePutLanguagePairConfiguration,
  useTutorConfigurations,
} from '../services/tutorConfigurationsApi';

interface LanguageOption {
  code: string;
  name: string;
  supportedAsNative: boolean;
  supportedAsTarget: boolean;
}

function languageLabel(code: string, languages: LanguageOption[]): string {
  const language = languages.find((candidate) => candidate.code === code);
  return language ? `${language.name} (${code})` : code.toUpperCase();
}

function voiceLabel(id: number, voices: VoiceResponse[]): string {
  const voice = voices.find((candidate) => candidate.id === id);
  if (!voice) return `Unknown voice · ID ${id}`;
  return `${voice.name} · ${voice.providerName} · ID ${id}`;
}

function filterCodes(
  configurations: TutorConfiguration[],
  pairConfigurations: LanguagePairConfiguration[],
  languages: LanguageOption[],
  axis: 'native' | 'target'
): string[] {
  const configuredCodes = configurations.map((configuration) =>
    axis === 'native' ? configuration.nativeLanguageCode : configuration.targetLanguageCode
  );
  const pairCodes = pairConfigurations.map((configuration) =>
    axis === 'native' ? configuration.nativeLanguageCode : configuration.targetLanguageCode
  );
  const supportedCodes = languages
    .filter((language) =>
      axis === 'native' ? language.supportedAsNative : language.supportedAsTarget
    )
    .map((language) => language.code);
  return Array.from(new Set([...supportedCodes, ...configuredCodes, ...pairCodes])).sort(
    (left, right) => languageLabel(left, languages).localeCompare(languageLabel(right, languages))
  );
}

function PairConfigurationEditor({
  nativeLanguageCode,
  targetLanguageCode,
  configuration,
  alternatives,
  voices,
}: {
  nativeLanguageCode: string;
  targetLanguageCode: string;
  configuration: LanguagePairConfiguration | undefined;
  alternatives: TutorConfiguration[];
  voices: VoiceResponse[];
}) {
  const putMutation = usePutLanguagePairConfiguration();
  const deleteMutation = useDeleteLanguagePairConfiguration();
  const [freePracticeEnabled, setFreePracticeEnabled] = useState(
    configuration?.freePracticeEnabled ?? false
  );
  const [voiceAssessmentEnabled, setVoiceAssessmentEnabled] = useState(
    configuration?.voiceAssessmentEnabled ?? false
  );
  const [assignment, setAssignment] = useState(configuration?.assignedAiTutorConfigurationId ?? '');
  const [error, setError] = useState<string | null>(null);
  const pending = putMutation.isPending || deleteMutation.isPending;

  const save = async () => {
    if ((freePracticeEnabled || voiceAssessmentEnabled) && !assignment) {
      setError('Choose an assigned Tutor configuration before enabling either feature.');
      return;
    }
    setError(null);
    try {
      await putMutation.mutateAsync({
        nativeLanguageCode,
        targetLanguageCode,
        freePracticeEnabled,
        voiceAssessmentEnabled,
        assignedAiTutorConfigurationId: assignment || null,
      });
      toast.success('Language-pair features updated');
    } catch (mutationError) {
      const message = getApiErrorMessage(mutationError);
      setError(message);
      toast.error(message);
    }
  };

  const remove = async () => {
    if (
      !window.confirm(
        `Remove feature configuration for ${nativeLanguageCode} → ${targetLanguageCode}?`
      )
    ) {
      return;
    }
    setError(null);
    try {
      await deleteMutation.mutateAsync({ nativeLanguageCode, targetLanguageCode });
      setFreePracticeEnabled(false);
      setVoiceAssessmentEnabled(false);
      setAssignment('');
      toast.success('Language-pair feature configuration removed');
    } catch (mutationError) {
      const message = getApiErrorMessage(mutationError);
      setError(message);
      toast.error(message);
    }
  };

  return (
    <section
      className="mb-6 rounded-lg border border-blue-100 bg-blue-50 p-4"
      aria-label="Language-pair features"
    >
      <h2 className="text-lg font-semibold text-gray-900">
        Features for {nativeLanguageCode} → {targetLanguageCode}
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        One assignment supplies the Tutor and Explainer voices for both new Free Practice and Voice
        Assessment sessions.
      </p>
      {error && (
        <p className="mt-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <label className="text-sm font-medium text-gray-700">
          Assigned configuration
          <select
            aria-label="Assigned Tutor configuration"
            value={assignment}
            onChange={(event) => setAssignment(event.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2"
          >
            <option value="">No assignment</option>
            {alternatives.map((alternative) => (
              <option key={alternative.id} value={alternative.id}>
                Tutor: {voiceLabel(alternative.voiceId, voices)} · Explainer:{' '}
                {voiceLabel(alternative.explainerVoiceId, voices)} · {alternative.id}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={freePracticeEnabled}
            onChange={(event) => setFreePracticeEnabled(event.target.checked)}
          />
          Free Practice enabled
        </label>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={voiceAssessmentEnabled}
            onChange={(event) => setVoiceAssessmentEnabled(event.target.checked)}
          />
          Voice Assessment enabled
        </label>
      </div>
      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={() => void save()}
          disabled={pending}
          className="rounded-md bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
        >
          Save pair configuration
        </button>
        {configuration && (
          <button
            type="button"
            onClick={() => void remove()}
            disabled={pending}
            className="rounded-md border border-red-300 px-4 py-2 text-red-700 disabled:opacity-50"
          >
            Remove pair configuration
          </button>
        )}
      </div>
    </section>
  );
}

export function TutorsListView() {
  const configurationsQuery = useTutorConfigurations();
  const pairConfigurationsQuery = useLanguagePairConfigurations();
  const languagesQuery = useLanguages();
  const voicesQuery = useVoices({ limit: 100 });
  const deleteMutation = useDeleteTutorConfiguration();
  const [nativeFilter, setNativeFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [dialogConfiguration, setDialogConfiguration] = useState<
    TutorConfiguration | null | undefined
  >(undefined);
  const [operationError, setOperationError] = useState<string | null>(null);

  const configurations = configurationsQuery.data ?? [];
  const pairConfigurations = pairConfigurationsQuery.data ?? [];
  const languages = languagesQuery.data ?? [];
  const voices = voicesQuery.data ?? [];
  const nativeCodes = filterCodes(configurations, pairConfigurations, languages, 'native');
  const targetCodes = filterCodes(configurations, pairConfigurations, languages, 'target');
  const filteredConfigurations = configurations.filter(
    (configuration) =>
      (!nativeFilter || configuration.nativeLanguageCode === nativeFilter) &&
      (!targetFilter || configuration.targetLanguageCode === targetFilter)
  );

  const isLoading =
    configurationsQuery.isLoading ||
    pairConfigurationsQuery.isLoading ||
    languagesQuery.isLoading ||
    voicesQuery.isLoading;
  const loadError =
    configurationsQuery.error ||
    pairConfigurationsQuery.error ||
    languagesQuery.error ||
    voicesQuery.error;

  const handleReload = () => {
    void Promise.all([
      configurationsQuery.refetch(),
      pairConfigurationsQuery.refetch(),
      languagesQuery.refetch(),
      voicesQuery.refetch(),
    ]);
  };

  const handleDelete = async (configuration: TutorConfiguration) => {
    const enabledWarning = pairConfigurations.some(
      (pair) => pair.assignedAiTutorConfigurationId === configuration.id
    )
      ? '\n\nThis configuration is assigned to a language pair. Reassign or remove that pair configuration first.'
      : '';
    const confirmed = window.confirm(
      `Delete Tutor configuration ${configuration.id} for ${configuration.nativeLanguageCode} → ${configuration.targetLanguageCode} (Tutor voice ID ${configuration.voiceId}, Explainer voice ID ${configuration.explainerVoiceId})?${enabledWarning}`
    );
    if (!confirmed) return;

    setOperationError(null);
    try {
      await deleteMutation.mutateAsync(configuration.id);
      toast.success('Tutor configuration deleted');
    } catch (error) {
      const message = getApiErrorMessage(error);
      setOperationError(message);
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        aria-label="Loading Tutor configurations"
      >
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="mb-2 text-lg text-red-600">Error loading Tutor configuration data</p>
          <p className="mb-4 text-gray-600">{getApiErrorMessage(loadError)}</p>
          <button
            type="button"
            onClick={handleReload}
            className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <DictionariesNavigation />

      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Tutor configurations</h1>
          <p className="mt-1 text-gray-600">
            Manage complete language-pair voice configurations for new Tutor sessions.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setOperationError(null);
            setDialogConfiguration(null);
          }}
          className="rounded-md bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
        >
          Add configuration
        </button>
      </div>

      {operationError && (
        <div
          role="alert"
          className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700"
        >
          {operationError}
        </div>
      )}

      <div className="mb-6 rounded-lg bg-white p-4 shadow-md">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label
              htmlFor="native-language-filter"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Native language
            </label>
            <select
              id="native-language-filter"
              value={nativeFilter}
              onChange={(event) => setNativeFilter(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All native languages</option>
              {nativeCodes.map((code) => (
                <option key={code} value={code}>
                  {languageLabel(code, languages)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="target-language-filter"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Target language
            </label>
            <select
              id="target-language-filter"
              value={targetFilter}
              onChange={(event) => setTargetFilter(event.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All target languages</option>
              {targetCodes.map((code) => (
                <option key={code} value={code}>
                  {languageLabel(code, languages)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {nativeFilter && targetFilter && (
        <PairConfigurationEditor
          key={`${nativeFilter}:${targetFilter}:${pairConfigurations.find((pair) => pair.nativeLanguageCode === nativeFilter && pair.targetLanguageCode === targetFilter)?.updatedAt ?? 'new'}`}
          nativeLanguageCode={nativeFilter}
          targetLanguageCode={targetFilter}
          configuration={pairConfigurations.find(
            (pair) =>
              pair.nativeLanguageCode === nativeFilter && pair.targetLanguageCode === targetFilter
          )}
          alternatives={configurations.filter(
            (configuration) =>
              configuration.nativeLanguageCode === nativeFilter &&
              configuration.targetLanguageCode === targetFilter
          )}
          voices={voices}
        />
      )}

      {filteredConfigurations.length === 0 ? (
        <div
          className="rounded-lg bg-white p-12 text-center shadow-md"
          data-testid="tutor-empty-state"
        >
          <p className="text-lg text-gray-600">
            {configurations.length === 0
              ? 'No Tutor configurations have been added.'
              : 'No Tutor configurations match the selected filters.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  'Native language',
                  'Target language',
                  'Tutor voice',
                  'Explainer voice',
                  'Assignment',
                  'Actions',
                ].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredConfigurations.map((configuration) => {
                const isRowPending =
                  deleteMutation.isPending && deleteMutation.variables === configuration.id;
                const assigned = pairConfigurations.some(
                  (pair) => pair.assignedAiTutorConfigurationId === configuration.id
                );

                return (
                  <tr key={configuration.id} data-testid={`tutor-row-${configuration.id}`}>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      {languageLabel(configuration.nativeLanguageCode, languages)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                      {languageLabel(configuration.targetLanguageCode, languages)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {voiceLabel(configuration.voiceId, voices)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {voiceLabel(configuration.explainerVoiceId, voices)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          assigned ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {assigned ? 'Assigned' : 'Alternative'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setOperationError(null);
                            setDialogConfiguration(configuration);
                          }}
                          disabled={isRowPending}
                          className="font-medium text-blue-600 hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label={`Edit ${configuration.nativeLanguageCode} to ${configuration.targetLanguageCode} configuration ${configuration.id}`}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(configuration)}
                          disabled={isRowPending}
                          className="font-medium text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label={`Delete ${configuration.nativeLanguageCode} to ${configuration.targetLanguageCode} configuration ${configuration.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dialogConfiguration !== undefined && (
        <TutorConfigurationDialog
          configuration={dialogConfiguration}
          languages={languages}
          voices={voices}
          onClose={() => setDialogConfiguration(undefined)}
        />
      )}
    </div>
  );
}
